import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useAdmin } from '../../../hooks/use-admin-context';
import { Configuration } from '../../../clients/quipli-service/configuration/configuration';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Constants } from '../../../constants/constants';
import Icon from '../../../components/icons/icon';
import { Dictionary, keyBy } from 'lodash';

export interface NavItemData {
  label: string; // The text to display in the navigation item
  link?: string; // The link to the navigation item
  absoluteLink?: string; // The absolute link to the navigation item
  icon: ReactElement; // The icon to display in the navigation item
  legacyLink?: boolean; // Whether the navigation item is a legacy link
  children?: NavItemData[]; // The children of the navigation item
  permission?: string[]; // The permissions required to access the navigation item
  configCheck?: string; // The configuration key to check
  configValue?: string; // The configuration value to check
  onItemClick?: () => void; // The function to call when the navigation item is clicked
  isCollapsed?: boolean; // Whether the navigation item is collapsed
}

export const useNavigation = () => {
  const { name, contextPath, hasPermissions } = useAdmin();
  const { getMultipleConfigurations } = useAPI();

  const [configurations, setConfigurations] = useState<Dictionary<Configuration>>();

  // Get configuration value
  const getConfiguration = useCallback(
    (key: string) => {
      return configurations?.[key]?.value;
    },
    [configurations],
  );

  // Load configurations
  useEffect(() => {
    const loadConfigs = async () => {
      try {
        const configs = await getMultipleConfigurations([
          'CONF_SITE_TAX_OPTIONS',
          'CONF_SITE_DELIVERY_OPTIONS',
          'CONF_ALLOW_SALE',
          'CONF_RENTER_TAG_PRICING',
          'CONF_RENT_ON_ACCOUNT',
          'CONF_WEBSITE_NAME',
          'CONF_FAVICON',
          'CONF_STRIPE_ACCOUNT_TYPE',
          'CONF_ACCOUNTING_BETA',
          'CONF_ACCOUNTING_PERIOD_CLOSE',
          'CONF_TAX_EXEMPT_REPORT',
          'CONF_DELIVERY_REVENUE_REPORT',
          'CONF_ENABLE_SERVICE_AND_REPAIR',
          'CONF_COMMISSION_ENABLED',
          'CONF_ENABLE_PURCHASE_ORDERS',
          'CONF_INSPECTIONS_ENABLED',
          'CONF_ENABLE_CREDIT_MEMO',
          'CONF_TAG_PRICING_V2',
        ]);
        setConfigurations(keyBy(configs, 'variable'));

        // Set web page title
        const title = configs.find((elem) => elem.variable === 'CONF_WEBSITE_NAME')?.value || '';
        const favicon = configs.find((elem) => elem.variable === 'CONF_FAVICON')?.value || '';

        document.title = `${title ? `${title} - ` : ''}Control Panel`;

        // Set favicon
        let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
        const path = window.location.href.split('/manager')[0];
        if (!link) {
          link = document.createElement('link');
          link.rel = 'shortcut icon';
          document.head.appendChild(link);
        }
        link.href = `${path}/image/site_favicon/${favicon}`;
      } catch (error) {
        console.error('Failed to load configurations:', error);
      }
    };

    loadConfigs();
  }, []);

  // Filtering function to check if the item should be shown
  const shouldShowItem = useCallback(
    (item: NavItemData): boolean => {
      // Check permissions
      if (item.permission && !item.permission.some((perm) => hasPermissions(perm))) {
        return false;
      }

      // Check configuration
      if (item.configCheck && item.configValue) {
        if (getConfiguration(item.configCheck) !== item.configValue) {
          return false;
        }
      }

      return true;
    },
    [hasPermissions, getConfiguration],
  );

  const allMainNavItems: NavItemData[] = [
    // Dashboard
    {
      label: 'Dashboard',
      link: 'home',
      icon: <Icon icon="Dashboard" stroke="var(--quipli-white-50)" />,
      permission: [Constants.Permissions.Dashboard_Sales_Revenue, Constants.Permissions.Dashboard_Orders_Products],
    },

    // Orders
    {
      label: 'Orders',
      icon: <Icon icon="ShoppingBag" stroke="var(--quipli-white-50)" />,
      permission: [Constants.Permissions.Customer_Order, Constants.Permissions.Extend_Call_Off_Requests],
      children: [
        {
          label: 'Orders',
          link: 'orders',
          icon: <></>,
          permission: [Constants.Permissions.Customer_Order],
        },
        {
          label: 'Create Order',
          link: 'orders/create',
          icon: <></>,
          permission: [Constants.Permissions.Customer_Order],
        },
        {
          label: 'Create Missed Rental',
          link: 'orders/createMissed',
          icon: <></>,
          permission: [Constants.Permissions.Customer_Order],
        },
        {
          label: 'Create Quote',
          link: 'orders/create_quote',
          icon: <></>,
          permission: [Constants.Permissions.Customer_Order],
        },
        // TODO: Uncomment this when we migrate call-off/extend orders to v2
        /* {
          label: 'Extension/Call-Off Requests',
          link: 'orderrefundshrink',
          icon: <></>,
          permission: [Constants.Permissions.Extend_Call_Off_Requests],
        }, */
      ],
    },

    // Dispatch
    {
      label: 'Dispatch',
      link: 'dispatch',
      icon: <Icon icon="Truck" stroke="var(--quipli-white-50)" />,
      permission: [Constants.Permissions.Customer_Order],
    },

    // Customers
    {
      label: 'Customers',
      icon: <Icon icon="Customer" stroke="var(--quipli-white-50)" />,
      permission: [Constants.Permissions.Customers_Users],
      children: [
        {
          label: 'Manage Customers',
          link: 'users',
          icon: <></>,
        },
        {
          label: 'Customer Reviews',
          link: 'reviews',
          icon: <></>,
        },
        {
          label: 'Representative Roles',
          link: 'representatives/roles',
          icon: <></>,
        },
        {
          label: 'On Account Requests',
          link: 'users/account_requests',
          icon: <></>,
          configCheck: 'CONF_RENT_ON_ACCOUNT',
          configValue: '1',
        },
        {
          label:
            configurations?.CONF_TAG_PRICING_V2?.value === '1' ? 'Global Customer Discounts' : 'Customer Tag Pricing',
          link: 'tagpricing',
          icon: <></>,
          configCheck: 'CONF_RENTER_TAG_PRICING',
          configValue: '1',
        },
      ],
    },

    // Inventory
    {
      label: 'Inventory',
      icon: <Icon icon="Inventory" stroke="var(--quipli-white-50)" />,
      permission: [
        Constants.Permissions.Manage_Products,
        Constants.Permissions.Inventory_Calendar,
        Constants.Permissions.Product_Categories,
      ],
      children: [
        {
          label: 'Products',
          link: 'products',
          icon: <></>,
          permission: [Constants.Permissions.Manage_Products],
        },
        {
          label: 'Product Units',
          link: 'productUnits',
          icon: <></>,
          permission: [Constants.Permissions.Manage_Products],
        },
        {
          label: 'Parts',
          link: 'inventory/parts',
          icon: <></>,
          permission: [Constants.Permissions.Manage_Products],
        },
        {
          label: 'Services',
          link: 'inventory/services',
          icon: <></>,
          permission: [Constants.Permissions.Manage_Products],
        },
        {
          label: 'Inventory Calendar',
          link: 'calendar',
          icon: <></>,
          permission: [Constants.Permissions.Inventory_Calendar],
        },
        {
          label: 'Categories',
          link: 'categories',
          icon: <></>,
          permission: [Constants.Permissions.Product_Categories],
        },
      ],
    },

    // Purchase Orders
    {
      label: 'Expenses',
      icon: <Icon icon="ShoppingBag2" stroke="var(--quipli-white-50)" />,
      configCheck: 'CONF_ENABLE_PURCHASE_ORDERS',
      configValue: '1',
      children: [
        { label: 'Purchase Orders', link: 'purchase-orders', icon: <></> },
        { label: 'Manage Vendors', link: 'vendors', icon: <></> },
      ],
      permission: [Constants.Permissions.Purchase_Orders],
    },

    // Service & Repair
    {
      label: 'Service & Repair',
      icon: <Icon icon="Tool02" stroke="var(--quipli-white-50)" />,
      permission: [Constants.Permissions.Work_Orders],
      configCheck: 'CONF_ENABLE_SERVICE_AND_REPAIR',
      configValue: '1',
      children: [
        { label: 'Work Orders', link: 'repair/work-orders', icon: <></> },
        { label: 'Scheduled Services', link: 'repair/scheduled-services', icon: <></> },
      ],
    },

    // Accounting
    {
      label: 'Accounting',
      icon: <Icon icon="Calculator" stroke="var(--quipli-white-50)" />,
      permission: [
        Constants.Permissions.Collectibles,
        Constants.Permissions.Accounting,
        Constants.Permissions.Quick_Books_Online,
      ],
      children: [
        {
          label: 'Collectibles',
          link: 'collectibles',
          icon: <></>,
          permission: [Constants.Permissions.Collectibles],
        },
        {
          label: 'General Ledger',
          link: 'accounting/accounts',
          icon: <></>,
          permission: [Constants.Permissions.Accounting],
          configCheck: 'CONF_ACCOUNTING_BETA',
          configValue: '1',
        },
        {
          label: 'General Ledger Settings',
          link: 'accounting/settings',
          icon: <></>,
          permission: [Constants.Permissions.Accounting],
          configCheck: 'CONF_ACCOUNTING_BETA',
          configValue: '1',
        },
        {
          label: 'Accounting Periods',
          link: 'accounting/accountingPeriods',
          icon: <></>,
          permission: [Constants.Permissions.Accounting],
          configCheck: 'CONF_ACCOUNTING_PERIOD_CLOSE',
          configValue: '1',
        },
        {
          label: 'Daily Payment Totals',
          link: 'reports/dailyPayments',
          icon: <></>,
          permission: [Constants.Permissions.Accounting],
        },
        {
          label: 'Tax',
          link: 'reports/tax',
          icon: <></>,
          permission: [Constants.Permissions.Accounting],
        },
        {
          label: 'Tax Exempt',
          link: 'reports/tax_exempt',
          icon: <></>,
          permission: [Constants.Permissions.Accounting],
          configCheck: 'CONF_TAX_EXEMPT_REPORT',
          configValue: '1',
        },
        {
          label: 'Payments',
          link: 'reports/payments',
          icon: <></>,
          permission: [Constants.Permissions.Accounting],
        },
        {
          label: 'Stripe Payments',
          link: 'quipli-pay/payments',
          icon: <></>,
          permission: [Constants.Permissions.Accounting],
          configCheck: 'CONF_STRIPE_ACCOUNT_TYPE',
          configValue: 'custom',
        },
        {
          label: 'Payouts',
          link: 'quipli-pay/payouts',
          icon: <></>,
          permission: [Constants.Permissions.Accounting],
          configCheck: 'CONF_STRIPE_ACCOUNT_TYPE',
          configValue: 'custom',
        },
        {
          label: 'Payout Reconciliation',
          link: 'reports/stripePayoutReconciliationReport',
          icon: <></>,
          permission: [Constants.Permissions.Accounting],
        },
        {
          label: 'Payment Method Configuration',
          link: 'payment-config/methods',
          icon: <></>,
          permission: [Constants.Permissions.Accounting],
        },
        {
          label: 'Tax Codes',
          link: 'taxcodes',
          icon: <></>,
          configCheck: 'CONF_SITE_TAX_OPTIONS',
          configValue: '5',
        },
        {
          label: 'QuickBooks Online Setup',
          link: 'qboaccount',
          icon: <></>,
          permission: [Constants.Permissions.Quick_Books_Online],
        },
      ],
    },

    // Credit Memos
    {
      label: 'Credit Memos',
      icon: <Icon icon="File" stroke="var(--quipli-white-50)" />,
      configCheck: 'CONF_ENABLE_CREDIT_MEMO',
      configValue: '1',
      children: [
        { label: 'Create Credit Memo', link: 'orders/createCreditMemo', icon: <></> },
        { label: 'Credit Memos', link: 'reports/creditMemos', icon: <></> },
      ],
    },

    // Reports
    {
      label: 'Reports',
      icon: <Icon icon="Report" stroke="var(--quipli-white-50)" />,
      permission: [Constants.Permissions.Reports],
      children: [
        { label: 'Daily Schedule', link: 'reports/dailySchedule', icon: <></> },
        { label: 'Transactions', link: 'reports/transactions', icon: <></> },
        {
          label: 'Delivery Revenue',
          link: 'reports/deliveryRevenue',
          icon: <></>,
          configCheck: 'CONF_DELIVERY_REVENUE_REPORT',
          configValue: '1',
        },
        { label: 'Renters', link: 'reports/users', icon: <></> },
        { label: 'Customer Searches', link: 'reports/customer-searches', icon: <></> },
        { label: 'Products', link: 'reports/products', icon: <></> },
        {
          label: 'Revenue',
          link: 'reports/revenue',
          icon: <></>,
        },
        {
          label: 'Sales Commission',
          link: 'reports/salesCommission',
          icon: <></>,
          permission: [Constants.Permissions.Staff_Members],
          configCheck: 'CONF_COMMISSION_ENABLED',
          configValue: '1',
        },
        { label: 'Missed Rentals', link: 'reports/missedRentals', icon: <></> },
      ],
    },

    // Customizations
    {
      label: 'Customizations',
      icon: <Icon icon="Customizations" stroke="var(--quipli-white-50)" />,
      permission: [
        Constants.Permissions.Navigations,
        Constants.Permissions.Content_Pages,
        Constants.Permissions.Content_Block,
        Constants.Permissions.Language_Labels,
        Constants.Permissions.FAQ_Categories,
        Constants.Permissions.FAQ_Management,
        Constants.Permissions.Cancel_Reasons,
        Constants.Permissions.Discount_Coupons,
        Constants.Permissions.Delivery_Locations,
        Constants.Permissions.Social_Platforms_Management,
      ],
      children: [
        {
          label: 'Web Navigations',
          link: 'navigations',
          icon: <></>,
          permission: [Constants.Permissions.Navigations],
        },
        {
          label: 'Content Pages',
          link: 'cms',
          icon: <></>,
          permission: [Constants.Permissions.Content_Pages],
        },
        {
          label: 'Content Block',
          link: 'extrapage',
          icon: <></>,
          permission: [Constants.Permissions.Content_Block],
        },
        {
          label: 'Custom Agree Types',
          link: 'customagreetypes',
          icon: <></>,
          permission: [Constants.Permissions.Custom_Agree_Types],
        },
        {
          label: 'Discount Coupons',
          link: 'coupons',
          icon: <></>,
          permission: [Constants.Permissions.Discount_Coupons],
        },
        {
          label: 'FAQ Category Management',
          link: 'faqcategories',
          icon: <></>,
          permission: [Constants.Permissions.FAQ_Categories],
        },
        {
          label: 'FAQ Management',
          link: 'faqs',
          icon: <></>,
          permission: [Constants.Permissions.FAQ_Management],
        },
        {
          label: 'Cancel Reasons',
          link: 'cancelreasons',
          icon: <></>,
          permission: [Constants.Permissions.Cancel_Reasons],
        },
        {
          label: 'Labels Text',
          link: 'labels',
          icon: <></>,
          permission: [Constants.Permissions.Language_Labels],
        },
        {
          label: 'Social Platforms',
          link: 'socialmedia',
          icon: <></>,
          permission: [Constants.Permissions.Social_Platforms_Management],
        },
        {
          label: 'Delivery Location',
          link: 'deliverylocations',
          icon: <></>,
          permission: [Constants.Permissions.Delivery_Locations],
        },
        {
          label: 'Zip Delivery Rates',
          link: 'zipdelivery',
          icon: <></>,
          configCheck: 'CONF_SITE_DELIVERY_OPTIONS',
          configValue: '3',
        },
      ],
    },

    // Settings
    {
      label: 'Settings',
      icon: <Icon icon="Settings" stroke="var(--quipli-white-50)" />,
      permission: [
        Constants.Permissions.General_Settings,
        Constants.Permissions.Currency_Management,
        Constants.Permissions.Manage_Shops,
        Constants.Permissions.Location_Attributes,
        Constants.Permissions.Product_Attributes,
        Constants.Permissions.Product_Options,
        Constants.Permissions.Filters,
        Constants.Permissions.Theme_Settings,
        Constants.Permissions.Manage_Email_Templates,
        Constants.Permissions.Bulk_Import_Export,
      ],
      children: [
        {
          label: 'General Settings',
          link: 'configurations',
          icon: <></>,
          permission: [Constants.Permissions.General_Settings],
        },
        {
          label: 'Stripe Setup',
          link: 'quipli-pay',
          icon: <></>,
          permission: [Constants.Permissions.Currency_Management],
          configCheck: 'CONF_STRIPE_ACCOUNT_TYPE',
          configValue: 'custom',
        },
        {
          label: 'Card Readers',
          link: 'quipli-pay/terminal',
          icon: <></>,
          permission: [Constants.Permissions.Currency_Management],
          configCheck: 'CONF_STRIPE_ACCOUNT_TYPE',
          configValue: 'custom',
        },
        {
          label: 'Bank',
          link: 'quipli-pay/bank',
          icon: <></>,
          permission: [Constants.Permissions.Currency_Management],
          configCheck: 'CONF_STRIPE_ACCOUNT_TYPE',
          configValue: 'custom',
        },
        {
          label: 'Locations',
          link: 'shops',
          icon: <></>,
          permission: [Constants.Permissions.Manage_Shops],
        },
        {
          label: 'Location Fees',
          link: 'shopattributes',
          icon: <></>,
          permission: [Constants.Permissions.Location_Attributes],
        },
        {
          label: 'Product Fees',
          link: 'productfeesattributes',
          icon: <></>,
          permission: [Constants.Permissions.Product_Attributes],
        },
        {
          label: 'Product Options',
          link: 'options/admin',
          icon: <></>,
          permission: [Constants.Permissions.Product_Options],
        },
        {
          label: 'Custom Product Builder',
          link: 'services',
          icon: <></>,
          permission: [Constants.Permissions.Product_Options],
          configCheck: 'CONF_ALLOW_SALE',
          configValue: '1',
        },
        {
          label: 'Filters',
          link: 'filters',
          icon: <></>,
          permission: [Constants.Permissions.Filters],
        },
        {
          label: 'Theme Settings',
          link: 'themes',
          icon: <></>,
          permission: [Constants.Permissions.Theme_Settings],
        },
        {
          label: 'Email Customizations',
          link: 'emailtemplatesv2',
          icon: <></>,
          permission: [Constants.Permissions.Manage_Email_Templates],
        },
        {
          label: 'Export / Import',
          link: 'importexport/default_action/export',
          icon: <></>,
          permission: [Constants.Permissions.Bulk_Import_Export],
        },
        {
          label: 'Inspection Workflows',
          link: 'inspectionWorkflows',
          icon: <></>,
          permission: [Constants.Permissions.General_Settings],
          configCheck: 'CONF_INSPECTIONS_ENABLED',
          configValue: '1',
        },
      ],
    },
  ];

  const allFooterNavItems: NavItemData[] = [
    {
      label: 'Admin Users',
      link: 'admins',
      icon: <Icon icon="AddCustomer" stroke="var(--quipli-white-50)" />,
      permission: [Constants.Permissions.Staff_Members],
    },
    {
      label: 'Support',
      icon: <Icon icon="Support" stroke="var(--quipli-white-50)" />,
      onItemClick: () => {
        window.zE.activate({ hideOnClose: true });
      },
    },
    {
      label: 'Quipli Help Center',
      absoluteLink: 'https://quiplihelp.zendesk.com/hc/en-us/',
      icon: <Icon icon="HelpCircle" stroke="var(--quipli-white-50)" />,
    },
    {
      label: 'Quipli Marketplace',
      absoluteLink: 'https://marketplace.quipli.com/',
      icon: <Icon icon="Store" fill="var(--quipli-white-50)" strokeWidth={1} />,
    },
    {
      label: 'Refer and Earn',
      absoluteLink: 'https://quipli.com/refer/',
      icon: <Icon icon="Refer" />,
    },
  ];

  const adminProfileNavItems: NavItemData[] = [
    {
      label: name || '',
      icon: (
        <img
          src={`${contextPath}image/user/small`}
          alt="Profile"
          style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
          }}
        />
      ),
      children: [
        { label: 'Profile', link: 'admin/profile', icon: <></> },
        { label: 'Change Password', link: 'admin/change_password', icon: <></> },
        { label: 'Log Out', link: 'admin/logout', legacyLink: true, icon: <></> },
      ],
    },
  ];

  const { mainNavItems: filteredMainNavItems, footerNavItems: filteredFooterNavItems } = useMemo(() => {
    // Filter items recursively
    const filterItems = (items: NavItemData[]): NavItemData[] => {
      return items
        .filter(shouldShowItem)
        .map((item) => ({
          ...item,
          children: item.children ? filterItems(item.children) : undefined,
        }))
        .filter((item) => !item.children || item.children.length > 0);
    };

    return {
      mainNavItems: filterItems(allMainNavItems),
      footerNavItems: allFooterNavItems.filter(shouldShowItem),
    };
  }, [shouldShowItem]);

  return {
    mainNavItems: filteredMainNavItems,
    footerNavItems: filteredFooterNavItems,
    adminProfileNavItems,
  };
};
